import "primevue/resources/themes/lara-light-indigo/theme.css";
/**
 * First we will load all of this project's JavaScript dependencies which
 * includes Vue and other libraries. It is a great starting point when
 * building robust, powerful web applications using Vue and Laravel.
 */
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import "./bootstrap";
import SearchBoxComponent from "./components/SearchBoxComponent.vue";
import ShoppingCartCountComponent from "./components/cart/ShoppingCartCounterComponent.vue";

import PrimeVue from "primevue/config";

import VueSnap from "vue-snap";
import "vue-snap/dist/vue-snap.css";

import { addIcons, OhVueIcon } from "oh-vue-icons";
import {
  BiTelephoneFill,
  BiTrophy,
  FaDownload,
  // Added missing icons for Marley Hire layout and index
  FaFacebook,
  FaSearch,
  FaShoppingCart,
  HiArrowRight,
  HiCalendar,
  HiCheck,
  HiCheckCircle,
  HiChevronRight,
  HiCog,
  HiDocumentDownload,
  HiDocumentText,
  HiExclamation,
  HiLocationMarker,
  HiPhone,
  HiPhotograph,
  HiRefresh,
  HiSearch,
  HiShieldCheck,
  HiShieldExclamation,
  HiShoppingCart,
  HiStar,
  HiTruck,
  HiTrash,
  HiUser,
  HiUsers,
  HiX,
  LaSearchSolid,
  LaTimesCircle,
  LaTimesSolid,
  MdMenu,
  RiCloseFill,
  FaInfoCircle,
  FaYoutube,
  FaTiktok,
} from "oh-vue-icons/icons";
import { createApp, defineAsyncComponent } from "vue";
import MobileMenu from "./components/MobileMenu.vue";

import ImageGrid from "./components/ImageGrid.vue";
import ImageScrollerVueSnap from "./components/ImageScrollerVueSnap.vue";
import PhoneButton from "./components/PhoneButton.vue";
import SearchFullScreen from "./components/SearchFullScreen.vue";
import TestimonialsVueSnap from "./components/TestimonialsVueSnap.vue";
// Cart popover
import CompactCartItem from "./components/cart/CompactCartItem.vue";
import ShoppingCart from "./components/cart/ShoppingCart.vue";

import CurrencyValue from "./components/CurrencyValue.vue";
import FeaturedCategoriesSlider from "./components/FeaturedCategoriesSlider.vue";
import NavbarDropdown from "./components/NavbarDropdown.vue";
import TurnstileWidget from "./components/TurnstileWidget.vue";

// Lazy-loaded: checkout components (only needed on checkout pages)
const CheckoutIndex = defineAsyncComponent(() => import("./components/checkout/CheckoutIndex.vue"));
const CustomerDetailsComponent = defineAsyncComponent(() => import("./components/checkout/CustomerDetailsComponent.vue"));
const BookingDetailsComponent = defineAsyncComponent(() => import("./components/checkout/booking/BookingDetailsComponent.vue"));
const BookingIndexComponent = defineAsyncComponent(() => import("./components/checkout/booking/BookingIndexComponent.vue"));
const StripePaymentCard = defineAsyncComponent(() => import("./components/StripePaymentCard.vue"));
const ConfirmBookingComponent = defineAsyncComponent(() => import("./components/ConfirmBookingComponent.vue"));
const PortalEnabledCheckoutComponent = defineAsyncComponent(() => import("./components/checkout/PortalEnabledCheckoutComponent.vue"));
const SubmitOrderRedirect = defineAsyncComponent(() => import("./components/checkout/sale/complete/SubmitOrderRedirect.vue"));
const ClearCart = defineAsyncComponent(() => import("./components/checkout/combined-complete/ClearCart.vue"));

// Lazy-loaded: product page components (only needed on product detail pages)
const AddToCartComponent = defineAsyncComponent(() => import("./components/AddToCartComponent.vue"));
const AddToSalesCartCompnent = defineAsyncComponent(() => import("./components/AddToSalesCartComponent.vue"));
const ImageGalleryWithThumbnails = defineAsyncComponent(() => import("./components/ImageGalleryWithThumbnails.vue"));
const ProductImageSlider = defineAsyncComponent(() => import("./components/ProductImageSlider.vue"));
const ShareProduct = defineAsyncComponent(() => import("./components/ShareProductComponent.vue"));
const DeliveryPricesModal = defineAsyncComponent(() => import("./components/DeliveryPricesModal.vue"));
const DeliveryPricesDiagram = defineAsyncComponent(() => import("./components/DeliveryPricesDiagram.vue"));

// Lazy-loaded: page-specific components
const ContactUsForm = defineAsyncComponent(() => import("./components/ContactUsForm.vue"));
const EquipmentActions = defineAsyncComponent(() => import("./components/EquipmentActions.vue"));
const EventPlanner = defineAsyncComponent(() => import("./components/EventPlanner.vue"));

const app = createApp({
  components: {
    "add-to-cart": AddToCartComponent,
    "cart-count": ShoppingCartCountComponent,
    "complete-booking": BookingIndexComponent,
    "customer-details": CustomerDetailsComponent,
    "booking-details": BookingDetailsComponent,
    "stripe-payment": StripePaymentCard,
    "confirm-booking": ConfirmBookingComponent,
    "search-box": SearchBoxComponent,
    "contact-us-form": ContactUsForm,
    "equipment-actions": EquipmentActions,
    "event-planner": EventPlanner,
    "add-to-sales-cart": AddToSalesCartCompnent,
    "submit-order-redirect": SubmitOrderRedirect,
    "clear-cart": ClearCart,
    "delivery-prices-modal": DeliveryPricesModal,
    "delivery-prices-diagram": DeliveryPricesDiagram,
    "turnstile-widget": TurnstileWidget,
    // AccountApplicationForm,
    CurrencyValue,
    CheckoutIndex,
    ImageGrid,
    ImageScrollerVueSnap,
    MobileMenu,
    ShoppingCart,
    CompactCartItem,
    PhoneButton,
    TestimonialsVueSnap,
    SearchFullScreen,
    NavbarDropdown,
    FeaturedCategoriesSlider,
    PortalEnabledCheckoutComponent,
    ImageGalleryWithThumbnails,
    ShareProduct,
    ProductImageSlider,
  },
});

addIcons(
  RiCloseFill,
  FaShoppingCart,
  LaSearchSolid,
  MdMenu,
  BiTelephoneFill,
  HiChevronRight,
  FaSearch,
  LaTimesSolid,
  LaTimesCircle,
  // Added missing icons for Marley Hire layout and index
  FaFacebook,
  HiShoppingCart,
  HiSearch,
  HiCalendar,
  HiDocumentText,
  HiDocumentDownload,
  HiTruck,
  HiUsers,
  HiUser,
  HiCheck,
  HiCheckCircle,
  HiExclamation,
  HiLocationMarker,
  HiPhone,
  HiPhotograph,
  HiRefresh,
  HiShieldCheck,
  HiShieldExclamation,
  HiX,
  HiTrash,
  HiCog,
  BiTrophy,
  HiStar,
  HiArrowRight,
  FaDownload,
  FaInfoCircle,
  FaYoutube,
  FaTiktok,
);

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);
app.use(pinia);
app.use(PrimeVue, {
  zIndex: {
    modal: 1100,
    overlay: 1300,
  },
});
app.use(VueSnap);
app.component("v-icon", OhVueIcon);

app.mount("#app");
